import { inject, Service } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { LanguageCacheService } from '@core/caching/language-cache.service';
import { LoggerService, ScopedLogger } from '@core/logging/logger.service';
import { PlatformService } from '@core/platform/platform.service';
import { AlertService } from '@shared/feedback/alert/alert.service';
import { apiErrorMessage } from '@core/http/api-error';

import { TranslationRepository } from '../domain/interfaces/translation.interface';
import { Language } from '../domain/language';
import {
  flattenTranslations,
  ILanguageTranslations,
  TranslationMap,
} from '../domain/translation.types';
import { LOCAL_TRANSLATIONS } from '../data/translations.dictionary';
import { I18N_CONFIG } from '../infrastructure/i18n.config';

/**
 * TranslationsService — merge orchestration over the language-scoped cache.
 *
 * Caching (memory → TransferState → localStorage, TTL, SSR safety, in-flight
 * de-dup) is fully delegated to {@link LanguageCacheService} via its generic
 * `getOrFetch` helper — keyed by `talbinah-portal-translations-{lang}-storage`.
 * This service is now only responsible for:
 *
 *   · talking to the source-agnostic {@link TranslationRepository} (mock/http),
 *   · merging API values over the bundled local dictionary, and
 *   · patching cached maps with any local keys added since they were stored.
 *
 * Provided at the application ROOT (via `provideI18n` in `app.config.ts`),
 * alongside the repository binding it resolves.
 */
@Service({ autoProvided: false })
export class TranslationsService {
  private readonly repo = inject(TranslationRepository);
  private readonly cache = inject(LanguageCacheService);
  private readonly config = inject(I18N_CONFIG);
  private readonly platform = inject(PlatformService);
  private readonly alerts = inject(AlertService);
  private readonly logger: ScopedLogger = inject(LoggerService).forSource('TranslationsService');

  /** Main entry — returns a flattened, merged translation map for `lang`. */
  getTranslations(lang: Language): Observable<TranslationMap> {
    return this.cache
      .getOrFetchTranslations<TranslationMap>(lang, () => this.fetchFromRepository(lang), {
        ttlMs: this.config.cacheTtlMs,
        useTransferState: this.config.useTransferState,
        persistToStorage: this.config.useLocalStorage,
      })
      // Cache may predate a newly-added local key — patch on the way out.
      .pipe(map((data) => this.patchWithLocalKeys(data, lang)));
  }

  /** Drop the cache for `lang` then re-fetch. */
  protected forceRefresh(lang: Language): Observable<TranslationMap> {
    this.cache.invalidateTranslations(lang);
    return this.getTranslations(lang);
  }

  /** Synchronous local-only map (used for instant fallback). */
  getLocalTranslations(lang: Language): TranslationMap {
    return flattenTranslations(LOCAL_TRANSLATIONS[lang] ?? {});
  }

  // ─── internals ──────────────────────────────────────────────────────────
  private fetchFromRepository(lang: Language): Observable<TranslationMap> {
    return this.repo.getTranslations(lang).pipe(
      map((res) => this.mergeWithLocal(res.data, lang)),
      catchError((err: unknown) => {
        // Graceful degrade to the bundled local dictionary — but TELL the user the
        // live translations couldn't load (they'll see the saved/default copy).
        this.logger.error('Translation source failed, using local dictionary', { lang, err });
        this.notifyLoadFailed(lang, err);
        return of(this.getLocalTranslations(lang));
      }),
    );
  }

  /**
   * Toast the translation-load failure: the API's `response.message` if it sent
   * one, else the bundled per-request fallback copy for `lang`. Resolved from the
   * LOCAL dictionary (never the failed remote), with a hard-coded safety net so a
   * missing key still shows readable prose. Browser-only. Handled inline here —
   * NOT via `ApiErrorAlerts` — because that helper injects the TranslationFacade,
   * which injects this service (an i18n DI cycle we must not create).
   */
  private notifyLoadFailed(lang: Language, err: unknown): void {
    if (!this.platform.isBrowser) return;
    const local = this.getLocalTranslations(lang);
    const fallback =
      local['reference_portal_error_load_translations'] ??
      (lang === Language.En
        ? 'Couldn’t load the app text from the server; the saved copy will be used.'
        : 'تعذّر تحميل نصوص التطبيق من الخادم، وسيتم استخدام النسخة المحفوظة.');
    this.alerts.error(apiErrorMessage(err, lang) ?? fallback);
  }

  /** API values win; local dictionary fills any missing/blank key. */
  private mergeWithLocal(apiData: ILanguageTranslations, lang: Language): TranslationMap {
    const local = this.getLocalTranslations(lang);
    const flattened = flattenTranslations(apiData);
    const merged: TranslationMap = { ...local };
    Object.keys(flattened).forEach((k) => {
      const v = flattened[k];
      if (v && v.trim()) merged[k] = v;
    });
    return merged;
  }

  /** Patch cached data with any local keys added since it was stored. */
  private patchWithLocalKeys(data: TranslationMap, lang: Language): TranslationMap {
    const local = this.getLocalTranslations(lang);
    const safe = data ?? {};
    const missing = Object.keys(local).filter((k) => !safe[k] || !safe[k].trim());
    if (missing.length === 0) return safe;
    const patched = { ...safe };
    missing.forEach((k) => (patched[k] = local[k]!));
    return patched;
  }
}
